import React, { useRef, useState, useEffect } from 'react';
import { getScheme } from '../data/colorSchemes';
import { TourTrigger } from './Tour';
import { DownloadFeedbackModal, trackDownload } from './FeedbackWidget';
import './PreviewPanel.css';

const PAGE_W_PX = 650;
const INCH_PX   = PAGE_W_PX / 8.5;
const PAGE_H_PX = Math.round(INCH_PX * 11);

const CARTOON_AVATAR = `data:image/svg+xml;utf8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
  <circle cx="50" cy="50" r="50" fill="#e8f2f2"/>
  <circle cx="50" cy="38" r="18" fill="#fcd5b0"/>
  <ellipse cx="44" cy="36" rx="2.5" ry="3" fill="#4a3728"/>
  <ellipse cx="56" cy="36" rx="2.5" ry="3" fill="#4a3728"/>
  <circle cx="44" cy="36" r="1" fill="#fff"/>
  <circle cx="56" cy="36" r="1" fill="#fff"/>
  <path d="M44 44 Q50 49 56 44" stroke="#c07050" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
  <ellipse cx="44" cy="31" rx="5" ry="2" fill="#7a5c3a" opacity="0.7"/>
  <ellipse cx="56" cy="31" rx="5" ry="2" fill="#7a5c3a" opacity="0.7"/>
  <ellipse cx="50" cy="72" rx="22" ry="15" fill="#2a6b6b"/>
  <circle cx="50" cy="57" r="8" fill="#fcd5b0"/>
</svg>`)}`;

const SVG_ICONS = {
  email: (color) => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
    </svg>
  ),
  phone: (color) => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.6 19.79 19.79 0 0 1 1.09 4.93 2 2 0 0 1 3 2.84h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 10a16 16 0 0 0 6.29 6.29l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.16 17z"/>
    </svg>
  ),
  location: (color, forced) => forced ? (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/>
    </svg>
  ) : (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="#e84040" stroke="#e84040" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3" fill="white" stroke="white"/>
    </svg>
  ),
  linkedin: (color, forced) => forced ? (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/>
    </svg>
  ) : (
    <svg width="12" height="12" viewBox="0 0 24 24">
      <rect x="0" y="0" width="24" height="24" rx="4" fill="#0077b5"/>
      <path d="M7 10v7" stroke="white" strokeWidth="2" strokeLinecap="round"/>
      <circle cx="7" cy="7" r="1.2" fill="white"/>
      <path d="M11 10v7M11 13c0-1.66 1.34-3 3-3s3 1.34 3 3v4" stroke="white" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
  website: (color) => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
    </svg>
  ),
};

// ─────────────────────────────────────────────────────────
// Shared experience entry renderer
// - Role and company are always block-level (separate lines)
// - roleOrder controls which comes first
// - When sub-roles are present, company is always the header
// - Date is absolutely positioned so it never affects text wrapping
// ─────────────────────────────────────────────────────────
function ExpEntry({ exp, fmt, expFmt, sectionStyles }) {
  const roleOrder   = sectionStyles?.experience?.roleOrder ?? 'company-first';
  const hasSubRoles = exp.subRoles && exp.subRoles.length > 0;
  // Bold is position-based: top field bold, bottom field normal.
  // Per-entry exp.formatting holds B-button overrides:
  //   bold:true  = explicit bold (valid for bottom field)
  //   bold:false = explicit un-bold (valid for top field)
  // Section-level bold is always stale — strip it.
  // For the BOTTOM field only: bold:false from per-entry is also stale (bottom is
  // never bold by position, so storing false is redundant old data) — strip it.
  // For the TOP field: bold:false is a valid user override — keep it.
  const _topField    = roleOrder === 'company-first' ? 'company' : 'role';
  const _bottomField = roleOrder === 'company-first' ? 'role' : 'company';

  const mergeField = (fieldName, sectionFmt, perEntry) => {
    const { bold: _sectionBold, ...base } = sectionFmt ?? {};
    const pe = perEntry ?? {};
    // Strip bold:false from the bottom field (stale), keep it for top field (valid override)
    if (fieldName === _bottomField && pe.bold === false) {
      const { bold: _stale, ...cleanPe } = pe;
      return { ...base, ...cleanPe };
    }
    return { ...base, ...pe };
  };

  expFmt = {
    company: mergeField('company', expFmt.company, exp.formatting?.company),
    role:    mergeField('role',    expFmt.role,    exp.formatting?.role),
    date:    { ...(expFmt.date ?? {}), ...(exp.formatting?.date ?? {}) },
  };
  const bullets     = exp.bullets.filter(b => b.trim());
  const dateStr     = [
    exp.startDate,
    exp.startDate && (exp.endDate || exp.current) ? ' – ' : '',
    exp.current ? 'Present' : exp.endDate,
  ].join('');

  // ── Sub-roles: company as bold header, each role indented below ──
  if (hasSubRoles) {
    return (
      <div className="r-entry">
        {exp.company && (
          <div className="r-entry-role" style={{ ...fmt(expFmt.company), fontWeight: 700 }}>{exp.company}</div>
        )}
        {exp.subRoles.map(sr => {
          const srDate = [
            sr.startDate,
            sr.startDate && (sr.endDate || sr.current) ? ' – ' : '',
            sr.current ? 'Present' : sr.endDate,
          ].join('');
          const srBullets = (sr.bullets || []).filter(b => b.trim());
          return (
            <div key={sr.id} style={{ marginTop: 4 }}>
              <div className="r-entry-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div className="r-entry-company" style={{ ...fmt(expFmt.role), minWidth: 0, flex: 1 }}>{sr.role}</div>
                {srDate && (
                  <span className="r-entry-date" style={{ ...fmt(expFmt.date), whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {srDate}
                  </span>
                )}
              </div>
              {srBullets.length > 0 && (
                <ul className="r-bullets">
                  {srBullets.map((b, i) => (
                    <li key={i} style={{ marginBottom: `${sectionStyles.experience.bulletSpacing}px` }}>{b}</li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // ── No sub-roles: role-first or company-first ──
  // Top line is always bold + has the date. Bottom line is always normal weight.
  // fontWeight is set explicitly inline so fmt() never overrides it.
  // Position sets the DEFAULT bold. B can override it.
  // Top: bold (600) by default; B=false explicitly removes it.
  // Bottom: normal by default; B=true explicitly adds it.
  // When order switches, FormPanel resets bold state so position takes over cleanly.
  const topStyle    = (f) => ({ fontWeight: f?.bold === false ? 'normal' : 600,    fontStyle: f?.italic ? 'italic' : 'normal', textDecoration: f?.underline ? 'underline' : 'none' });
  const bottomStyle = (f) => ({ fontWeight: f?.bold === true  ? 'bold'   : 'normal', fontStyle: f?.italic ? 'italic' : 'normal', textDecoration: f?.underline ? 'underline' : 'none' });

  if (roleOrder === 'company-first') {
    return (
      <div className="r-entry">
        <div className="r-entry-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          <div className="r-entry-role" style={{ ...topStyle(expFmt.company), minWidth: 0, flex: 1 }}>{exp.company}</div>
          {dateStr && (
            <span className="r-entry-date" style={{ ...fmt(expFmt.date), whiteSpace: 'nowrap', flexShrink: 0 }}>
              {dateStr}
            </span>
          )}
        </div>
        {exp.role && <div className="r-entry-company" style={bottomStyle(expFmt.role)}>{exp.role}</div>}
        {bullets.length > 0 && (
          <ul className="r-bullets">
            {bullets.map((b, i) => (
              <li key={i} style={{ marginBottom: `${sectionStyles.experience.bulletSpacing}px` }}>{b}</li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  // role-first (default)
  return (
    <div className="r-entry">
      <div className="r-entry-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div className="r-entry-role" style={{ ...topStyle(expFmt.role), minWidth: 0, flex: 1 }}>{exp.role}</div>
        {dateStr && (
          <span className="r-entry-date" style={{ ...fmt(expFmt.date), whiteSpace: 'nowrap', flexShrink: 0 }}>
            {dateStr}
          </span>
        )}
      </div>
      {exp.company && <div className="r-entry-company" style={bottomStyle(expFmt.company)}>{exp.company}</div>}
      {bullets.length > 0 && (
        <ul className="r-bullets">
          {bullets.map((b, i) => (
            <li key={i} style={{ marginBottom: `${sectionStyles.experience.bulletSpacing}px` }}>{b}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Shared education entry renderer
// ─────────────────────────────────────────────────────────
function EduEntry({ edu, fmt, eduFmt }) {
  const dateStr = [
    edu.startDate,
    edu.startDate && edu.endDate ? ' – ' : '',
    edu.endDate,
  ].join('');

  return (
    <div className="r-education-entry">
      <div className="r-entry-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          {(edu.degree || edu.field) && (
            <span className="r-entry-role" style={fmt(eduFmt.degree)}>{[edu.degree, edu.field].filter(Boolean).join(' in ')}</span>
          )}
          {edu.school && (
            <span className="r-entry-company" style={fmt(eduFmt.school)}>
              {(edu.degree || edu.field) ? ' · ' : ''}{edu.school}
            </span>
          )}
          {edu.gpa && <span className="r-entry-company"> — GPA {edu.gpa}</span>}
        </div>
        {dateStr && (
          <span className="r-entry-date" style={{ ...fmt(eduFmt.date), whiteSpace: 'nowrap', flexShrink: 0 }}>
            {dateStr}
          </span>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Misc sections renderer (shared across layouts)
// ─────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────
// renderMiscEntry — shared citation-aware entry renderer
// Used by both MiscSections and OrderedSections
// ─────────────────────────────────────────────────────────
function renderMiscEntry(entry, sec, pubIdx) {
  const isPub    = /publication|journal|conference|paper/i.test(sec.title || '');
  const citeFmt  = sec.citeFmt || 'apa';

  if (!isPub) {
    return (
      <div key={entry.id} className="r-entry" style={{ marginBottom: 8 }}>
        <div className="r-entry-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            {entry.title    && <div className="r-entry-role" style={{ fontWeight: 600 }}>{entry.title}</div>}
            {entry.subtitle && <div className="r-entry-company">{entry.subtitle}</div>}
          </div>
          {entry.date && <span className="r-entry-date" style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>{entry.date}</span>}
        </div>
        {entry.description && <p style={{ margin: '3px 0 0', fontSize: '0.9em', color: '#444' }}>{entry.description}</p>}
      </div>
    );
  }

  // ── IEEE: [N] Authors, "Title," Journal, year. ──
  if (citeFmt === 'ieee') {
    return (
      <div key={entry.id} className="r-entry" style={{ marginBottom: 10, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
        <span style={{ flexShrink: 0, color: '#555', fontSize: '0.88em', fontWeight: 600 }}>[{pubIdx}]</span>
        <p style={{ margin: 0, fontSize: '0.88em', color: '#333', lineHeight: 1.55 }}>
          {entry.description && <span>{entry.description}, </span>}
          {entry.title       && <span>"{entry.title}," </span>}
          {entry.subtitle    && <span style={{ fontStyle: 'italic' }}>{entry.subtitle}</span>}
          {entry.date        && <span>, {entry.date}.</span>}
        </p>
      </div>
    );
  }

  // APA: Authors (Year). / Title. / Journal.
  // MLA: Authors. / "Title." / Journal, Year.
  return (
    <div key={entry.id} className="r-entry" style={{ marginBottom: 10 }}>
      {entry.description && (
        <p style={{ margin: '0 0 1px', fontSize: '0.88em', color: '#555' }}>
          {citeFmt === 'apa' && entry.date
            ? `${entry.description} (${entry.date}).`
            : `${entry.description}.`}
        </p>
      )}
      {entry.title && (
        <p style={{ margin: '0 0 1px', fontWeight: 600, color: '#1a1a1a', fontSize: '1em' }}>
          {citeFmt === 'mla' ? `"${entry.title}."` : `${entry.title}.`}
        </p>
      )}
      {entry.subtitle && (
        <p style={{ margin: 0, fontSize: '0.88em', color: '#555' }}>
          {citeFmt === 'mla' && entry.date
            ? `${entry.subtitle}, ${entry.date}.`
            : `${entry.subtitle}.`}
        </p>
      )}
    </div>
  );
}

function MiscSections({ misc, accent, noColor, bodyFont, fontSize, lineHeight }) {
  if (!misc || misc.length === 0) return null;
  const titleSize = Math.max(9, fontSize - 1);
  return (
    <>
      {misc.map(sec => (
        sec.title && sec.entries && sec.entries.length > 0 && (
          <div key={sec.id} className="r-section" style={{ fontFamily: bodyFont, fontSize }}>
            <div className="r-section-title" style={{ color: accent, borderColor: noColor ? '#ddd' : accent + '55', fontSize: titleSize }}>
              {sec.title || 'Miscellaneous'}
            </div>
            {sec.entries.map((entry, ei) => renderMiscEntry(entry, sec, ei + 1))}
          </div>
        )
      ))}
    </>
  );
}

// ─────────────────────────────────────────────────────────
// OrderedSections — renders experience/education/skills/misc
// in the order defined by pageSettings.sectionOrder,
// respecting pageSettings.hiddenSections.
// ─────────────────────────────────────────────────────────
function OrderedSections({ resume, sectionStyles, pageSettings, accent, noColor, bodyFont, fmt, expFmt, eduFmt, skFmt, expTitleSize, eduTitleSize, skipSections = [] }) {
  const { experience, education, skills, misc = [] } = resume;
  const order  = pageSettings?.sectionOrder   ?? ['experience', 'education', 'skills'];
  const hidden = pageSettings?.hiddenSections ?? [];
  const miscKeys = misc.filter(s => s.title).map(s => 'misc:' + s.id);
  const newMisc  = miscKeys.filter(k => !order.includes(k));
  const fullOrder = [...order.filter(k =>
    k === 'experience' || k === 'education' || k === 'skills' ||
    (k.startsWith('misc:') && misc.some(s => s.id === parseInt(k.split(':')[1])))
  ), ...newMisc];

  return (
    <>
      {fullOrder.map(key => {
        if (hidden.includes(key)) return null;
              if (skipSections.includes(key)) return null;
        if (key === 'experience' && experience.length > 0) return (
          <div key="experience" className="r-section" style={{ fontFamily: bodyFont, fontSize: `${sectionStyles.experience.fontSize}px` }}>
            <div className="r-section-title" style={{ color: accent, borderColor: noColor ? '#ddd' : accent + '55', fontSize: `${expTitleSize}px` }}>Experience</div>
            {experience.map(exp => <ExpEntry key={exp.id} exp={exp} fmt={fmt} expFmt={expFmt} sectionStyles={sectionStyles} />)}
          </div>
        );
        if (key === 'education' && education.length > 0) return (
          <div key="education" className="r-section" style={{ fontFamily: bodyFont, fontSize: `${sectionStyles.education.fontSize}px` }}>
            <div className="r-section-title" style={{ color: accent, borderColor: noColor ? '#ddd' : accent + '55', fontSize: `${eduTitleSize}px` }}>Education</div>
            {education.map(edu => <EduEntry key={edu.id} edu={edu} fmt={fmt} eduFmt={eduFmt} />)}
          </div>
        );
        if (key === 'skills' && skills.length > 0) return (
          <div key="skills" className="r-section" style={{ fontFamily: bodyFont }}>
            <div className="r-section-title" style={{ color: accent, borderColor: noColor ? '#ddd' : accent + '55', fontSize: `${eduTitleSize}px` }}>Skills</div>
            {sectionStyles.skills.separator === 'comma' ? (
              <p className="r-skills-comma" style={fmt(skFmt)}>{skills.join(', ')}</p>
            ) : (
              <p className="r-skills-marker">
                {skills.map((s, i) => (
                  <span key={s} style={fmt(skFmt)}>
                    {i > 0 && <span className="r-skill-dot" style={{ color: accent }}>▪</span>}
                    {s}
                  </span>
                ))}
              </p>
            )}
          </div>
        );
        if (key.startsWith('misc:')) {
          const id  = parseInt(key.split(':')[1]);
          const sec = misc.find(s => s.id === id);
          if (!sec || !sec.title || !sec.entries?.length) return null;
          return (
            <div key={key} className="r-section" style={{ fontFamily: bodyFont, fontSize: sectionStyles.experience?.fontSize ?? 13 }}>
              <div className="r-section-title" style={{ color: accent, borderColor: noColor ? '#ddd' : accent + '55', fontSize: Math.max(9, (sectionStyles.experience?.fontSize ?? 13) - 1) }}>
                {sec.title}
              </div>
              {sec.entries.map((entry, ei) => renderMiscEntry(entry, sec, ei + 1))}
            </div>
          );
        }
        return null;
      })}
    </>
  );
}

// ─────────────────────────────────────────────────────────
// ResumeBody — standard single-column layout
// ─────────────────────────────────────────────────────────
export function ResumeBody({ resume, sectionStyles, mLeft, mRight, lineHeight, personalStyles, colorScheme, bodyFont, pageSettings }) {
  const { personal, experience, education, skills, misc = [] } = resume;
  const hasContent = personal.name || personal.email || personal.phone ||
                     personal.title || personal.location || personal.linkedin ||
                     personal.website || personal.summary ||
                     experience.length > 0 || education.length > 0 || skills.length > 0 || misc.length > 0;
  const align             = personalStyles.headerAlign ?? 'left';
  const showIcons         = personalStyles.showIcons ?? true;
  const scheme            = getScheme(colorScheme);
  const accent            = scheme.accent;
  const noColor           = colorScheme === 'none';
  const iconColor         = personalStyles.iconColor ?? 'default';
  const forced            = iconColor === 'accent';
  const iconCol           = forced ? accent : '#555';
  const headerFont        = personalStyles.headerFont ?? 'DM Serif Display';
  const headerNameSize    = personalStyles.headerNameSize ?? 32;
  const headerContactSize = personalStyles.headerContactSize ?? 12;

  const fmt = (f) => f ? {
    fontWeight:     f.bold      ? 'bold'      : 'normal',
    fontStyle:      f.italic    ? 'italic'    : 'normal',
    textDecoration: f.underline ? 'underline' : 'none',
  } : {};

  const expFmt = sectionStyles.experience?.formatting ?? {};
  const eduFmt = sectionStyles.education?.formatting  ?? {};
  const skFmt  = sectionStyles.skills?.formatting     ?? {};

  const expTitleSize = Math.max(9, sectionStyles.experience.fontSize - 1);
  const eduTitleSize = Math.max(9, sectionStyles.education.fontSize  - 1);

  return (
    <div className="resume-body" style={{ paddingLeft: mLeft, paddingRight: mRight, lineHeight }}>
      {!hasContent ? (
        <div className="resume-empty">
          <div className="resume-empty-icon">📄</div>
          <p>Start filling in the form to see your resume here.</p>
        </div>
      ) : (
        <>
          <div className="r-header" style={{ textAlign: align, borderBottom: `2.5px solid ${noColor ? '#1a1a1a' : accent}` }}>
            {personal.name  && <h1 className="r-name" style={{ fontFamily: headerFont, fontSize: `${headerNameSize}px` }}>{personal.name}</h1>}
            {personal.title && <p className="r-title">{personal.title}</p>}
            <div className="r-contact" style={{ justifyContent: align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center', fontSize: `${headerContactSize}px` }}>
              {personal.email    && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>{showIcons && SVG_ICONS.email(iconCol, forced)}{personal.email}</span>}
              {personal.phone    && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>{showIcons && SVG_ICONS.phone(iconCol, forced)}{personal.phone}</span>}
              {personal.location && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>{showIcons && SVG_ICONS.location(iconCol, forced)}{personal.location}</span>}
              {personal.linkedin && <span data-pdf-link={personal.linkedin} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>{showIcons && SVG_ICONS.linkedin(iconCol, forced)}{(personal.linkedinLabel || personal.linkedin.replace(/^https?:\/\/(www\.)?/, ''))}</span>}
              {personal.website  && <span data-pdf-link={personal.website} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>{showIcons && SVG_ICONS.website(iconCol, forced)}{(personal.websiteLabel || personal.website.replace(/^https?:\/\/(www\.)?/, ''))}</span>}
            </div>
          </div>

          {personal.summary && (
            <div className="r-section" style={{ fontFamily: bodyFont }}>
              <div className="r-section-title" style={{ color: accent, borderColor: noColor ? '#ddd' : accent + '55', fontSize: `${expTitleSize}px` }}>Summary</div>
              <p className="r-summary">{personal.summary}</p>
            </div>
          )}

          <OrderedSections resume={resume} sectionStyles={sectionStyles} pageSettings={pageSettings} accent={accent} noColor={noColor} bodyFont={bodyFont} fmt={fmt} expFmt={expFmt} eduFmt={eduFmt} skFmt={skFmt} expTitleSize={expTitleSize} eduTitleSize={eduTitleSize} />
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Sidebar layout
// ─────────────────────────────────────────────────────────
export function SidebarBody({ resume, sectionStyles, mLeft, mRight, lineHeight, colorScheme, personalStyles, bodyFont, pageSettings, cutPoints = [], onMeasureSkillStart, measuredSkillStartY }) {
  const { personal, experience, education, skills, misc = [] } = resume;
  const scheme            = getScheme(colorScheme);
  const accent            = scheme.accent;
  const sidebarBg         = scheme.sidebarBg;
  const sidebarBorder     = scheme.sidebarBorder;
  const noColor           = colorScheme === 'none';
  const sidebarW          = '32%';
  const showIcons         = personalStyles?.showIcons ?? true;
  const iconColor         = personalStyles?.iconColor ?? 'default';
  const forced            = iconColor === 'accent';
  const iconCol           = forced ? accent : '#555';
  const headerFont        = personalStyles?.headerFont ?? 'DM Serif Display';
  const headerNameSize    = personalStyles?.headerNameSize ?? 32;
  const headerContactSize = personalStyles?.headerContactSize ?? 12;

  const expFmt = sectionStyles.experience?.formatting ?? {};
  const eduFmt = sectionStyles.education?.formatting  ?? {};
  const skFmt  = sectionStyles.skills?.formatting     ?? {};

  const expTitleSize = Math.max(9, sectionStyles.experience.fontSize - 1);
  const eduTitleSize = Math.max(9, sectionStyles.education.fontSize  - 1);

  // ── Sidebar skill layout ──────────────────────────────
  // Measure where the first skill starts (Y in content coords).
  // Use that + actual cut points to calculate per-page skill heights
  // so that skill rows never straddle a page boundary.
  const sidebarRef    = React.useRef(null);
  const firstSkillRef = React.useRef(null);

  const mTop  = Math.round((pageSettings?.marginTop    ?? 1.0) * INCH_PX);
  const mBot  = Math.round((pageSettings?.marginBottom ?? 1.0) * INCH_PX);
  const areaH = PAGE_H_PX - mTop - mBot;
  const PREFERRED_H = 22;

  // Measure skillStartY only in the measure host instance, then pass up to PreviewPanel
  React.useEffect(() => {
    if (!onMeasureSkillStart || !firstSkillRef.current || !sidebarRef.current || skills.length === 0) return;
    const sidebarTop = sidebarRef.current.getBoundingClientRect().top;
    const skillTop   = firstSkillRef.current.getBoundingClientRect().top;
    onMeasureSkillStart(Math.round(skillTop - sidebarTop));
  }, [skills.length, onMeasureSkillStart]);

  // Use measured value from measure host; fall back to null until measured
  const skillStartY = measuredSkillStartY ?? null;

  // Build per-page skill groups once we have skillStartY and cutPoints
  const skillPages = React.useMemo(() => {
    const hidden = (pageSettings?.hiddenSections ?? []).includes('skills');
    if (hidden || skills.length === 0 || skillStartY === null) return null;

    const pages = [];
    let idx = 0;

    // Page 0: distribute evenly so skills fill exactly to the cut point.
    // spacer goes AFTER last skill to push the next page's first skill to the cut.
    if (cutPoints.length > 0) {
      const avail  = cutPoints[0] - skillStartY;
      const count  = Math.max(1, Math.floor(avail / PREFERRED_H));
      const height = Math.floor(avail / count);
      const spacer = avail - count * height;
      const take   = Math.min(count, skills.length - idx);
      if (take > 0) { pages.push({ startIdx: idx, take, height, spacer }); idx += take; }
    } else {
      const avail  = areaH - skillStartY;
      const count  = Math.max(1, Math.floor(avail / PREFERRED_H));
      const height = Math.floor(avail / count);
      const spacer = avail - count * height;
      const take   = Math.min(count, skills.length - idx);
      if (take > 0) { pages.push({ startIdx: idx, take, height, spacer }); idx += take; }
    }

    // Page 2+: same formula as page 0.
    // CRITICAL: spacer must be non-zero to align next group to the next cut point.
    // Without spacer, the next group starts BEFORE the cut point, causing
    // the first skill of that group to straddle the page boundary.
    let cutIdx = 1;
    while (idx < skills.length) {
      const sliceStart = cutPoints[cutIdx - 1] ?? 0;
      const sliceEnd   = cutPoints[cutIdx]     ?? (sliceStart + areaH);
      const avail      = sliceEnd - sliceStart;
      const count      = Math.max(1, Math.floor(avail / PREFERRED_H));
      const spacer     = avail - count * PREFERRED_H;
      const take       = Math.min(count, skills.length - idx);
      pages.push({ startIdx: idx, take, height: PREFERRED_H, spacer });
      idx += take;
      cutIdx++;
    }

    return pages;
  }, [skillStartY, cutPoints, skills.length, areaH, pageSettings?.hiddenSections]);

  const fmt = (f) => f ? {
    fontWeight:     f.bold      ? 'bold'      : 'normal',
    fontStyle:      f.italic    ? 'italic'    : 'normal',
    textDecoration: f.underline ? 'underline' : 'none',
  } : {};

  const sectionTitle = (title, size) => (
    <div className="r-section-title" style={{
      fontSize: size ?? 10, fontWeight: 700, letterSpacing: '0.1em',
      textTransform: 'uppercase', color: accent,
      borderBottom: `1.5px solid ${accent}`,
      paddingBottom: 3, marginBottom: 8, marginTop: 14,
    }}>{title}</div>
  );

  const skillItem = (s, i, height, isLast) => sectionStyles.skills?.separator === 'marker' ? (
    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10.5, color: '#333', boxSizing: 'border-box', height, borderTop: i === 0 ? 'none' : '1px solid #e8e8e8', borderBottom: isLast ? '1px solid #e8e8e8' : 'none' }}>
      <span style={{ color: accent, fontSize: 8 }}>▪</span>
      <span style={fmt(skFmt)}>{s}</span>
    </div>
  ) : (
    <div key={i} style={{ display: 'flex', alignItems: 'center', fontSize: 10.5, color: '#333', boxSizing: 'border-box', height, borderTop: i === 0 ? 'none' : '1px solid #e8e8e8', borderBottom: isLast ? '1px solid #e8e8e8' : 'none', ...fmt(skFmt) }}>{s}</div>
  );

  return (
    <div ref={sidebarRef} style={{ display: 'flex', paddingLeft: mLeft, paddingRight: mRight, lineHeight, minHeight: '100%' }}>
      <div style={{ width: sidebarW, flexShrink: 0, background: sidebarBg, borderRight: `1px solid ${sidebarBorder}`, padding: '20px 16px 20px 12px', boxSizing: 'border-box' }}>
        {(personal.photo || true) && (
          <div style={{ textAlign: 'center', marginBottom: 14 }}>
            <img src={personal.photo || CARTOON_AVATAR} alt="Headshot" style={{ width: 90, height: 90, borderRadius: '50%', objectFit: 'cover', border: `3px solid ${accent}` }} />
          </div>
        )}
        <div style={{ textAlign: 'center', marginBottom: 12 }}>
          {personal.name  && <div style={{ fontFamily: headerFont, fontWeight: 700, fontSize: Math.round(headerNameSize * 0.5), color: '#1a1a1a', lineHeight: 1.2 }}>{personal.name}</div>}
          {personal.title && <div style={{ fontSize: 11, color: '#666', marginTop: 3 }}>{personal.title}</div>}
        </div>
        {(personal.email || personal.phone || personal.location || personal.linkedin || personal.website) && (
          <>
            {sectionTitle('Contact')}
            {[
              personal.email    && { icon: SVG_ICONS.email(iconCol, forced),    text: personal.email },
              personal.phone    && { icon: SVG_ICONS.phone(iconCol, forced),    text: personal.phone },
              personal.location && { icon: SVG_ICONS.location(iconCol, forced), text: personal.location },
              personal.linkedin && { icon: SVG_ICONS.linkedin(iconCol, forced), text: (personal.linkedinLabel || personal.linkedin.replace(/^https?:\/\/(www\.)?/, '')), url: personal.linkedin },
              personal.website  && { icon: SVG_ICONS.website(iconCol, forced),  text: (personal.websiteLabel || personal.website.replace(/^https?:\/\/(www\.)?/, '')), url: personal.website },
            ].filter(Boolean).map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 11, color: '#333', marginBottom: 5, wordBreak: 'break-word' }}>
                {showIcons && <span style={{ flexShrink: 0, marginTop: 1 }}>{item.icon}</span>}
                <span {...(item.url ? { 'data-pdf-link': item.url } : {})}>{item.text}</span>
              </div>
            ))}
          </>
        )}
        {skills.length > 0 && !(pageSettings?.hiddenSections ?? []).includes('skills') && (
          <>
            {sectionTitle('Skills')}
            {/* firstSkillRef on a zero-height marker so position is stable */}
            <div ref={firstSkillRef} style={{ height: 0 }} />
            {skillPages ? (
              skillPages.map((page, pi) => (
                <React.Fragment key={pi}>
                  {skills.slice(page.startIdx, page.startIdx + page.take).map((s, i) =>
                    skillItem(s, i, page.height, page.startIdx + i === skills.length - 1)
                  )}
                  {page.spacer > 0 && <div style={{ height: page.spacer }} />}
                </React.Fragment>
              ))
            ) : (
              skills.map((s, i) => skillItem(s, i, PREFERRED_H))
            )}
          </>
        )}
      </div>
      <div style={{ flex: 1, paddingLeft: 18, paddingTop: 20, paddingBottom: 20, boxSizing: 'border-box' }}>
        {personal.summary && (
          <>
            {sectionTitle('Summary', expTitleSize)}
            <p style={{ fontSize: 11.5, color: '#333', marginBottom: 12, lineHeight: 1.6 }}>{personal.summary}</p>
          </>
        )}
        <OrderedSections resume={resume} sectionStyles={sectionStyles} pageSettings={pageSettings} accent={accent} noColor={noColor} bodyFont={bodyFont} fmt={fmt} expFmt={expFmt} eduFmt={eduFmt} skFmt={skFmt} expTitleSize={expTitleSize} eduTitleSize={eduTitleSize} skipSections={['skills']} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Executive Photo layout
// ─────────────────────────────────────────────────────────
export function ExecutivePhotoBody({ resume, sectionStyles, mLeft, mRight, lineHeight, personalStyles, colorScheme, photoPosition, bodyFont, pageSettings }) {
  const { personal, experience, education, skills, misc = [] } = resume;
  const scheme            = getScheme(colorScheme);
  const accent            = scheme.accent;
  const noColor           = colorScheme === 'none';
  const align             = personalStyles.headerAlign ?? 'center';
  const showIcons         = personalStyles.showIcons ?? true;
  const iconColor         = personalStyles.iconColor ?? 'default';
  const forced            = iconColor === 'accent';
  const iconCol           = forced ? accent : '#555';
  const headerFont        = personalStyles.headerFont ?? 'DM Serif Display';
  const headerNameSize    = personalStyles.headerNameSize ?? 32;
  const headerContactSize = personalStyles.headerContactSize ?? 12;
  const photoSide         = photoPosition ?? 'left';
  const PHOTO_SIZE        = 72;

  const expFmt = sectionStyles.experience?.formatting ?? {};
  const eduFmt = sectionStyles.education?.formatting  ?? {};
  const skFmt  = sectionStyles.skills?.formatting     ?? {};

  const expTitleSize = Math.max(9, sectionStyles.experience.fontSize - 1);
  const eduTitleSize = Math.max(9, sectionStyles.education.fontSize  - 1);

  const fmt = (f) => f ? {
    fontWeight:     f.bold      ? 'bold'      : 'normal',
    fontStyle:      f.italic    ? 'italic'    : 'normal',
    textDecoration: f.underline ? 'underline' : 'none',
  } : {};

  return (
    <div className="resume-body" style={{ paddingLeft: mLeft, paddingRight: mRight, lineHeight }}>
      <div style={{ display: 'flex', flexDirection: photoSide === 'right' ? 'row-reverse' : 'row', alignItems: 'center', gap: 18, marginBottom: 12 }}>
        <img src={personal.photo || CARTOON_AVATAR} alt="Headshot" style={{ width: PHOTO_SIZE, height: PHOTO_SIZE, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: `2.5px solid ${accent}` }} />
        <div style={{ flex: 1, textAlign: align }}>
          {personal.name  && <h1 className="r-name" style={{ fontFamily: headerFont, fontSize: `${headerNameSize}px` }}>{personal.name}</h1>}
          {personal.title && <p className="r-title">{personal.title}</p>}
          <div className="r-contact" style={{ justifyContent: align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center', fontSize: `${headerContactSize}px` }}>
            {personal.email    && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>{showIcons && SVG_ICONS.email(iconCol, forced)}{personal.email}</span>}
            {personal.phone    && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>{showIcons && SVG_ICONS.phone(iconCol, forced)}{personal.phone}</span>}
            {personal.location && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>{showIcons && SVG_ICONS.location(iconCol, forced)}{personal.location}</span>}
            {personal.linkedin && <span data-pdf-link={personal.linkedin} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>{showIcons && SVG_ICONS.linkedin(iconCol, forced)}{(personal.linkedinLabel || personal.linkedin.replace(/^https?:\/\/(www\.)?/, ''))}</span>}
            {personal.website  && <span data-pdf-link={personal.website} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>{showIcons && SVG_ICONS.website(iconCol, forced)}{(personal.websiteLabel || personal.website.replace(/^https?:\/\/(www\.)?/, ''))}</span>}
          </div>
        </div>
      </div>
      <hr style={{ border: 'none', borderTop: `2px solid ${accent}`, margin: '0 0 12px 0' }} />
      {personal.summary && (
        <div className="r-section" style={{ fontFamily: bodyFont }}>
          <div className="r-section-title" style={{ color: accent, borderColor: noColor ? '#ddd' : accent + '55', fontSize: `${expTitleSize}px` }}>Summary</div>
          <p className="r-summary">{personal.summary}</p>
        </div>
      )}
      <OrderedSections resume={resume} sectionStyles={sectionStyles} pageSettings={pageSettings} accent={accent} noColor={noColor} bodyFont={bodyFont} fmt={fmt} expFmt={expFmt} eduFmt={eduFmt} skFmt={skFmt} expTitleSize={expTitleSize} eduTitleSize={eduTitleSize} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Navy Editorial layout
// ─────────────────────────────────────────────────────────
export function NavyEditorialBody({ resume, sectionStyles, mLeft, mRight, lineHeight, personalStyles, colorScheme, bodyFont, pageSettings }) {
  const { personal, experience, education, skills, misc = [] } = resume;
  const hasContent = personal.name || personal.email || personal.phone ||
                     personal.title || personal.location || personal.linkedin ||
                     personal.website || personal.summary ||
                     experience.length > 0 || education.length > 0 || skills.length > 0;

  const scheme            = getScheme(colorScheme);
  const accent            = scheme.accent;
  const noColor           = colorScheme === 'none';
  const headerFont        = personalStyles?.headerFont        ?? 'DM Serif Display';
  const headerNameSize    = personalStyles?.headerNameSize    ?? 32;
  const headerContactSize = personalStyles?.headerContactSize ?? 12;

  const expFmt = sectionStyles.experience?.formatting ?? {};
  const eduFmt = sectionStyles.education?.formatting  ?? {};
  const skFmt  = sectionStyles.skills?.formatting     ?? {};

  const expTitleSize = Math.max(9, sectionStyles.experience.fontSize - 1);
  const eduTitleSize = Math.max(9, sectionStyles.education.fontSize  - 1);

  const fmt = (f) => f ? {
    fontWeight:     f.bold      ? 'bold'      : 'normal',
    fontStyle:      f.italic    ? 'italic'    : 'normal',
    textDecoration: f.underline ? 'underline' : 'none',
  } : {};

  const WHITE = '#ffffff';

  return (
    <div className="resume-body" style={{ paddingLeft: 0, paddingRight: 0, lineHeight }}>
      {!hasContent ? (
        <div className="resume-empty" style={{ paddingLeft: mLeft, paddingRight: mRight }}>
          <div className="resume-empty-icon">📄</div>
          <p>Start filling in the form to see your resume here.</p>
        </div>
      ) : (
        <>
          <div style={{ background: noColor ? '#1a1a1a' : accent, paddingLeft: mLeft, paddingRight: mRight, paddingTop: 20, paddingBottom: 16 }}>
            {personal.name && (
              <h1 style={{ fontFamily: headerFont, fontSize: `${headerNameSize}px`, fontWeight: 400, color: WHITE, letterSpacing: '0.01em', lineHeight: 1.15, margin: 0 }}>
                {personal.name}
              </h1>
            )}
            {personal.title && (
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', fontWeight: 400, letterSpacing: '0.06em', textTransform: 'uppercase', margin: '4px 0 0 0' }}>
                {personal.title}
              </p>
            )}
            {(personal.email || personal.phone || personal.location || personal.linkedin || personal.website) && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', marginTop: 10, fontSize: `${headerContactSize}px`, color: 'rgba(255,255,255,0.9)' }}>
                {personal.email    && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>{SVG_ICONS.email(WHITE, true)}{personal.email}</span>}
                {personal.phone    && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>{SVG_ICONS.phone(WHITE, true)}{personal.phone}</span>}
                {personal.location && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>{SVG_ICONS.location(WHITE, true)}{personal.location}</span>}
                {personal.linkedin && <span data-pdf-link={personal.linkedin} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>{SVG_ICONS.linkedin(WHITE, true)}{(personal.linkedinLabel || personal.linkedin.replace(/^https?:\/\/(www\.)?/, ''))}</span>}
                {personal.website  && <span data-pdf-link={personal.website} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>{SVG_ICONS.website(WHITE, true)}{(personal.websiteLabel || personal.website.replace(/^https?:\/\/(www\.)?/, ''))}</span>}
              </div>
            )}
          </div>
          <div style={{ paddingLeft: mLeft, paddingRight: mRight, paddingTop: 8 }}>
            {personal.summary && (
              <div className="r-section" style={{ fontFamily: bodyFont }}>
                <div className="r-section-title" style={{ color: accent, borderColor: noColor ? '#ddd' : accent + '55', fontSize: `${expTitleSize}px` }}>Summary</div>
                <p className="r-summary">{personal.summary}</p>
              </div>
            )}
            <OrderedSections resume={resume} sectionStyles={sectionStyles} pageSettings={pageSettings} accent={accent} noColor={noColor} bodyFont={bodyFont} fmt={fmt} expFmt={expFmt} eduFmt={eduFmt} skFmt={skFmt} expTitleSize={expTitleSize} eduTitleSize={eduTitleSize} />
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Main panel
// ─────────────────────────────────────────────────────────
export default function PreviewPanel({ resume, sectionStyles, pageSettings, onChangeTemplate, onCompareTemplate, onBackToProject, onGoToCoverLetter, onUndo, canUndo, onRedo, canRedo, onPageCount, onStartTour }) {
  const measureRef = useRef();
  const [layout, setLayout] = useState({ bodyHeight: PAGE_H_PX, cutPoints: [] });

  useEffect(() => {
    const el = measureRef.current;
    if (!el) return;

    let cancelled = false;

    // With font-display:swap, document.fonts.ready fires too early.
    // Explicitly load the exact font faces used in the resume, then
    // measure after two RAFs to ensure the swap reflow has settled.
    const measure = () => {
      if (cancelled) return;
      requestAnimationFrame(() => {
        if (cancelled) return;
        requestAnimationFrame(() => {
          if (cancelled) return;

    const elRect = el.getBoundingClientRect();
    const totalH = el.scrollHeight;

    const mT    = Math.round(pageSettings.marginTop    * INCH_PX);
    const mB    = Math.round(pageSettings.marginBottom * INCH_PX);
    const areaH = PAGE_H_PX - mT - mB;
    if (areaH <= 0) { setLayout({ bodyHeight: totalH, cutPoints: [] }); return; }

    // ── Step 1: true content height (excluding trailing margins) ──
    const LEAF_SEL = '.r-entry, .r-education-entry, .r-section-title, .r-skills-comma, .r-skills-marker, .r-summary, .r-bullets li';
    let contentH = 0;
    for (const elem of el.querySelectorAll(LEAF_SEL)) {
      const b = Math.ceil(elem.getBoundingClientRect().bottom - elRect.top);
      if (b > contentH) contentH = b;
    }
    if (contentH === 0) contentH = totalH;

    const rawPages = Math.ceil((contentH - 2) / areaH);
    if (rawPages <= 1) { setLayout({ bodyHeight: totalH, cutPoints: [] }); return; }

    // ── Step 2: collect safe break positions ──
    //
    // KEY PRINCIPLE: only cut at element/line TOPs, never at bottoms.
    // Cutting at a TOP means "this element starts the next page" — the cut
    // lands in whitespace BEFORE the element's text begins. This guarantees
    // no text element is ever split across the cut.
    //
    // We use Range.getClientRects() to get actual visual line tops (one rect
    // per rendered line), which is more accurate than computed lineHeight.

    const breakSet = new Set();
    const range    = document.createRange();

    // Block-level element TOPS — cutting here pushes the whole block to next page
    const BLOCK_SEL = [
      '.r-header', '.r-section', '.r-section-title',
      '.r-entry', '.r-education-entry', '.r-entry-header',
      '.r-bullets li', '.r-entry > p', '.r-section > p',
      '.r-summary', '.r-skills-comma', '.r-skills-marker',
    ].join(', ');

    for (const elem of el.querySelectorAll(BLOCK_SEL)) {
      const top = Math.floor(elem.getBoundingClientRect().top - elRect.top);
      if (top > 0) breakSet.add(top);
    }

    // Visual line TOPS from Range.getClientRects() — cutting here pushes that
    // line to the next page. Never cut at line BOTTOMS (would clip the line).
    const TEXT_SEL = [
      '.r-summary', '.r-bullets li', '.r-skills-comma', '.r-skills-marker',
      '.r-entry-role', '.r-entry-company',
      '.r-entry > p', '.r-section > p',
    ].join(', ');

    for (const elem of el.querySelectorAll(TEXT_SEL)) {
      try {
        range.selectNodeContents(elem);
        for (const rect of range.getClientRects()) {
          if (rect.width < 2) continue; // skip empty/zero-width rects
          const lineTop = Math.floor(rect.top - elRect.top);
          if (lineTop > 0) breakSet.add(lineTop);
        }
      } catch (e) { /* skip if range fails */ }
    }

    // Sort all break candidates
    const breaks = Array.from(breakSet).filter(v => v > 0).sort((a, b) => a - b);

    // ── Step 3: pick best cut — latest break TOP at or before idealCut ──
    // Subtract safety margin so sub-pixel rendering differences don't cause
    // partial text to show at the bottom of a page.
    const SAFETY = 6;
    const lastAtOrBefore = (max, min) => {
      let best = null;
      for (const v of breaks) {
        if (v > min && v <= max) best = v;
        if (v > max) break;
      }
      return best;
    };

    const newCuts  = [];
    let searchFrom = 0;

    for (let page = 1; page < rawPages; page++) {
      const idealCut = page * areaH - SAFETY;
      const bestCut  = lastAtOrBefore(idealCut, searchFrom);
      // If no break found, use idealCut (never raw page boundary which could split text)
      newCuts.push(bestCut ?? idealCut);
      searchFrom = bestCut ?? idealCut;
    }

    setLayout({ bodyHeight: totalH, cutPoints: newCuts });
        }); // end second RAF
      }); // end first RAF
    }; // end measure

    // Explicitly load all font faces used in the resume before measuring.
    // This is necessary because font-display:swap causes text to render with
    // fallback fonts first — document.fonts.ready resolves too early.
    const fontSize = '13px';
    Promise.all([
      document.fonts.load(fontSize + ' "DM Sans"'),
      document.fonts.load('600 ' + fontSize + ' "DM Sans"'),
      document.fonts.load(fontSize + ' "DM Serif Display"'),
    ]).then(measure).catch(measure); // always measure even if fonts fail

    return () => { cancelled = true; };
  }, [resume, sectionStyles, pageSettings]);

  const { bodyHeight, cutPoints } = layout;
  const [sidebarSkillStartY, setSidebarSkillStartY] = React.useState(null);
  const [dlFeedback, setDlFeedback] = useState(null); // filename or null
  const mTop    = Math.round(pageSettings.marginTop    * INCH_PX);
  const mBottom = Math.round(pageSettings.marginBottom * INCH_PX);
  const mLeft   = Math.round(pageSettings.marginLeft   * INCH_PX);
  const mRight  = Math.round(pageSettings.marginRight  * INCH_PX);
  const numPages   = cutPoints.length + 1;
  useEffect(() => { if (onPageCount) onPageCount(numPages); }, [numPages, onPageCount]);
  const pageSlices = Array.from({ length: numPages }, (_, i) => ({
    start: i === 0 ? 0 : cutPoints[i - 1],
    end:   i < cutPoints.length ? cutPoints[i] : bodyHeight,
  }));

  const lineHeight     = pageSettings.lineHeight ?? 1.6;
  const colorScheme    = pageSettings.colorScheme ?? 'teal';
  const bodyFont       = pageSettings.bodyFont ?? 'DM Sans';
  const personalStyles = sectionStyles.personal ?? { headerAlign: 'left', showIcons: true };
  const photoPosition  = pageSettings.photoPosition ?? 'left';
  const layoutType     = pageSettings.layout ?? 'standard';
  const bodyProps      = { resume, sectionStyles, mLeft, mRight, lineHeight, personalStyles, colorScheme, bodyFont, pageSettings, cutPoints };
  const BodyComponent  = layoutType === 'sidebar'         ? SidebarBody
                       : layoutType === 'executive-photo' ? ExecutivePhotoBody
                       : layoutType === 'navy-editorial'  ? NavyEditorialBody
                       : ResumeBody;
  const extraProps     = layoutType === 'executive-photo' ? { photoPosition } : {};
  const hasContent     = resume.personal.name || resume.personal.email || resume.personal.phone ||
                         resume.personal.title || resume.personal.location || resume.personal.linkedin ||
                         resume.personal.website || resume.personal.summary ||
                         resume.experience.length > 0 || resume.education.length > 0 || resume.skills.length > 0;

  const handleDownload = async () => {
    try {
      const { default: html2canvas } = await import('html2canvas');
      const { jsPDF } = await import('jspdf');
      const el   = measureRef.current;
      const rect = el.getBoundingClientRect();
      const contentCanvas = await html2canvas(el, {
        scale: 4, useCORS: true, backgroundColor: '#ffffff',
        scrollX: -rect.left, scrollY: -rect.top, windowWidth: PAGE_W_PX,
      });
      const scale  = 4;
      const pageW  = Math.round(PAGE_W_PX * scale);
      const pageH  = Math.round(PAGE_H_PX * scale);
      const mTopPx = Math.round(mTop * scale);
      const pdf    = new jsPDF({ orientation: 'portrait', unit: 'in', format: 'letter' });
      const scaledCuts = cutPoints.map(c => Math.round(c * scale));

      // Collect hyperlink elements from measure host before page loop
      const linkEls = Array.from(el.querySelectorAll('[data-pdf-link]'));
      const pdfLinks = linkEls.map(linkEl => {
        const lr  = linkEl.getBoundingClientRect();
        let url   = linkEl.getAttribute('data-pdf-link') || '';
        if (url && !url.startsWith('http')) url = 'https://' + url;
        const PAD = 4; // extra px to make link easier to click
        return {
          url,
          x: lr.left - rect.left,
          y: lr.top  - rect.top  - PAD,
          w: lr.width,
          h: lr.height + PAD * 2,
        };
      }).filter(l => l.url);

      // Conversion factors: content pixels → PDF inches
      const xScale = 8.5 / PAGE_W_PX;
      const yScale = 11  / PAGE_H_PX;

      for (let i = 0; i < numPages; i++) {
        if (i > 0) pdf.addPage();
        const srcY = i === 0 ? 0 : scaledCuts[i - 1];
        const srcH = (i < scaledCuts.length ? scaledCuts[i] : contentCanvas.height) - srcY;
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = pageW; pageCanvas.height = pageH;
        const ctx = pageCanvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, pageW, pageH);
        if (srcH > 0) ctx.drawImage(contentCanvas, 0, srcY, pageW, srcH, 0, mTopPx, pageW, srcH);
        pdf.addImage(pageCanvas.toDataURL('image/jpeg', 0.92), 'JPEG', 0, 0, 8.5, 11);
        pageCanvas.width = 0;

        // Add hyperlink annotations for links visible on this page
        const sliceStart = i === 0 ? 0 : cutPoints[i - 1];
        const sliceEnd   = i < cutPoints.length ? cutPoints[i] : bodyHeight;
        for (const link of pdfLinks) {
          if (link.y >= sliceStart && link.y < sliceEnd) {
            const xIn = link.x * xScale;
            const yIn = (mTop + (link.y - sliceStart)) * yScale;
            const wIn = link.w * xScale;
            const hIn = link.h * yScale;
            pdf.link(xIn, yIn, wIn, hIn, { url: link.url });
          }
        }
      }
      const fileName = `${resume.personal.name || 'resume'}.pdf`;
      pdf.save(fileName);
      trackDownload();
      setDlFeedback(fileName);
    } catch (err) {
      console.error('PDF export failed', err);
      alert('PDF export failed: ' + err.message);
    }
  };

  return (
    <div className="preview-panel">
      <div className="preview-toolbar">
        <div className="preview-toolbar-left">
          <button className="btn-back-to-project" onClick={onBackToProject}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
            </svg>
            My Project
          </button>
          {onStartTour && <TourTrigger onClick={onStartTour} />}
        </div>
        <div className="preview-toolbar-actions">
          <div className="btn-undo-redo-group">
            {onUndo && (
              <button className="btn-undo" onClick={onUndo} disabled={!canUndo} title="Undo last change">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <path d="M3 7v6h6"/><path d="M3 13C5 8 9.5 5 15 5c5 0 9 4 9 9s-4 9-9 9c-4 0-7.5-2-9-5"/>
                </svg>
                Undo
              </button>
            )}
            {onRedo && (
              <button className="btn-redo" onClick={onRedo} disabled={!canRedo} title="Redo last change">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <path d="M21 7v6h-6"/><path d="M21 13C19 8 14.5 5 9 5c-5 0-9 4-9 9s4 9 9 9c4 0 7.5-2 9-5"/>
                </svg>
                Redo
              </button>
            )}
          </div>
          <button className="btn-go-to-cl" onClick={onGoToCoverLetter}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
            Go to Cover Letter
          </button>
          <button className="btn-compare-template" onClick={onCompareTemplate} disabled={!hasContent}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
              <rect x="2" y="3" width="9" height="18" rx="1"/><rect x="13" y="3" width="9" height="18" rx="1"/>
            </svg>
            Compare
          </button>
          <button className="btn-change-template" onClick={onChangeTemplate}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
              <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
              <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
            </svg>
            Change Template
          </button>
          <button className="btn-download" onClick={handleDownload} disabled={!hasContent}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Download PDF
          </button>
        </div>
      </div>

      <div className="measure-host" ref={measureRef}>
        <BodyComponent {...bodyProps} {...extraProps} onMeasureSkillStart={layoutType === 'sidebar' ? setSidebarSkillStartY : undefined} />
      </div>

      <div className="preview-scroll" style={{ position: 'relative' }}>
        <div className="preview-canvas-label">
          <div>Live Preview</div>
          <div className="preview-canvas-page-count">{numPages} {numPages === 1 ? 'page' : 'pages'}</div>
        </div>
        <div className="pages-stack" style={{ width: PAGE_W_PX }}>
          {Array.from({ length: numPages }, (_, i) => (
            <React.Fragment key={i}>
              <div className="page-sheet" style={{ height: PAGE_H_PX }}>
                <div className="page-clip" style={{ top: layoutType === 'navy-editorial' && i === 0 ? 0 : mTop, height: Math.min(pageSlices[i].end - pageSlices[i].start, PAGE_H_PX - mTop - mBottom) }}>
                  <div className="page-clip-inner" style={{ top: -pageSlices[i].start }}>
                    <BodyComponent {...bodyProps} {...extraProps} measuredSkillStartY={layoutType === 'sidebar' ? sidebarSkillStartY : undefined} />
                  </div>
                </div>
              </div>
              {i < numPages - 1 && (
                <div className="page-gap">
                  <div className="page-gap-rule" />
                  <span className="page-gap-label">Page {i + 2}</span>
                  <div className="page-gap-rule" />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {dlFeedback && (
        <DownloadFeedbackModal
          fileName={dlFeedback}
          onClose={() => setDlFeedback(null)}
        />
      )}
    </div>
  );
}
